import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Send, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import LangSwitcher from '@/components/LangSwitcher';
import { trackEvent, estimateReadingTime, classifyCmsCta } from '@/lib/analytics';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { useLang } from '@/context/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CmsPage() {
    const { slug } = useParams();
    const { lang } = useLang();
    const [page, setPage] = useState(null);
    const [error, setError] = useState(null);
    const articleRef = useRef(null);
    const arrivalRef = useRef(Date.now());

    useEffect(() => {
        let active = true;
        setPage(null); setError(null);
        arrivalRef.current = Date.now();
        const load = async () => {
            try {
                if (lang === 'en') {
                    try {
                        const r = await axios.get(`${API}/pages/${slug}-en`);
                        if (active) setPage(r.data);
                        return;
                    } catch { /* pas de version EN → fallback FR */ }
                }
                const r = await axios.get(`${API}/pages/${slug}`);
                if (active) setPage(r.data);
            } catch (e) {
                if (active) setError(e.response?.status === 404 ? 'not_found' : 'error');
            }
        };
        load();
        return () => { active = false; };
    }, [slug, lang]);

    // Plausible — pageview with slug prop
    useEffect(() => {
        if (page) trackEvent('cms_pageview', { slug });
    }, [page, slug]);

    // Plausible — time-on-page on unload (rounded to 15s buckets to limit cardinality)
    useEffect(() => {
        const onUnload = () => {
            const sec = Math.round((Date.now() - arrivalRef.current) / 1000);
            if (sec >= 5) {
                const bucket = sec >= 240 ? '4m+' : sec >= 120 ? '2-4m' : sec >= 60 ? '1-2m' : sec >= 30 ? '30-60s' : '5-30s';
                trackEvent('cms_time_on_page', { slug, bucket });
            }
        };
        window.addEventListener('beforeunload', onUnload);
        return () => { window.removeEventListener('beforeunload', onUnload); onUnload(); };
    }, [slug]);

    // Plausible — delegated CTA click tracking on the article body
    useEffect(() => {
        const node = articleRef.current;
        if (!node) return;
        const onClick = (e) => {
            const a = e.target.closest && e.target.closest('a[href]');
            if (!a) return;
            const cta = classifyCmsCta(a.getAttribute('href'));
            if (cta) trackEvent('cms_cta_click', { slug, cta, label: (a.innerText || '').slice(0, 40) });
        };
        node.addEventListener('click', onClick);
        return () => node.removeEventListener('click', onClick);
    }, [slug, page]);

    const readingMin = useMemo(() => page ? estimateReadingTime(page.content_html) : 0, [page]);

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="cms-logo-home">
                        <img src="/african-valley-logo.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover ring-1 ring-border shadow-soft" />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl font-semibold tracking-tight">African</span>
                            <span className="font-heading text-2xl text-primary font-medium">Valley</span>
                            <span className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/50 ml-1.5 hidden sm:inline">Projets 360°</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LangSwitcher />
                        <Link to="/" className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-1">
                            <ArrowLeft size={14} /> Accueil
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full">
                <div className="max-w-3xl w-full mx-auto px-6 py-14 lg:py-20">
                {error === 'not_found' && (
                    <div className="text-center py-20" data-testid="cms-not-found">
                        <h1 className="font-heading text-3xl mb-2">Page introuvable</h1>
                        <p className="text-muted-foreground">Cette page n'existe pas ou a été retirée.</p>
                    </div>
                )}
                {error === 'error' && (
                    <div className="text-center py-20 text-destructive" data-testid="cms-error">Erreur de chargement</div>
                )}
                {!page && !error && (
                    <div className="text-center py-20 text-muted-foreground">Chargement…</div>
                )}
                {page && (
                    <>
                        {readingMin > 0 && (
                            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-foreground/50 mb-6" data-testid="cms-reading-time">
                                <Clock size={12} /> {readingMin} min de lecture
                            </div>
                        )}
                        <article
                            ref={articleRef}
                            className="prose prose-stone max-w-none prose-lg
                                prose-headings:font-heading prose-headings:tracking-tight
                                prose-h1:text-5xl prose-h1:lg:text-6xl prose-h1:mb-3 prose-h1:leading-[1.05] prose-h1:font-medium
                                prose-h2:text-3xl prose-h2:lg:text-4xl prose-h2:mt-16 prose-h2:mb-5 prose-h2:font-medium prose-h2:leading-tight prose-h2:pt-2
                                prose-h3:text-xl prose-h3:lg:text-2xl prose-h3:mt-10 prose-h3:mb-3 prose-h3:font-semibold
                                prose-p:leading-[1.85] prose-p:text-foreground/80 prose-p:my-5
                                prose-strong:text-foreground prose-strong:font-semibold
                                prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                                prose-ul:my-5 prose-ul:space-y-2 prose-li:leading-[1.7] prose-li:text-foreground/80 prose-li:marker:text-primary
                                prose-blockquote:border-l-2 prose-blockquote:border-primary prose-blockquote:bg-surface-muted/40 prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:text-foreground/85 prose-blockquote:rounded-r
                                prose-hr:my-12 prose-hr:border-border
                                prose-img:rounded-2xl prose-img:my-10"
                            data-testid={`cms-page-${slug}`}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content_html || '') }}
                        />
                    </>
                )}

                {/* Contact form injected after content on /p/contact */}
                {slug === 'contact' && page && <ContactForm />}
                </div>
            </main>

            <Footer />
        </div>
    );
}

function ContactForm() {
    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '', organization: '',
        subject: '', message: '', website: '',
    });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.message.trim().length < 10) {
            toast.error('Votre message doit faire au moins 10 caractères'); return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/contact/send`, form);
            setSent(true);
            toast.success('Message envoyé');
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        setBusy(false);
    };

    if (sent) {
        return (
            <div className="mt-16 card-soft p-10 text-center" data-testid="contact-form-success">
                <div className="h-14 w-14 rounded-full bg-primary-light text-primary mx-auto flex items-center justify-center mb-4">
                    <CheckCircle2 size={28} />
                </div>
                <h2 className="font-heading text-2xl mb-2">Message bien reçu !</h2>
                <p className="text-foreground/70 max-w-md mx-auto">
                    Nous revenons vers vous sous <strong>48 heures ouvrées</strong>. En attendant,
                    découvrez l'<Link to="/p/about" className="text-primary hover:underline">histoire d'African Valley</Link>.
                </p>
            </div>
        );
    }

    return (
        <div className="mt-16 card-soft p-7 lg:p-10" data-testid="contact-form">
            <div className="mb-6">
                <div className="eyebrow text-primary mb-2">Écrivez-nous</div>
                <h2 className="font-heading text-3xl">Envoyez votre message</h2>
                <p className="text-foreground/65 mt-2 text-sm">Réponse garantie sous 48 h ouvrées.</p>
            </div>

            <form onSubmit={submit} className="space-y-5">
                {/* Honeypot */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
                    <label>Site web<input type="text" tabIndex={-1} autoComplete="off"
                        onChange={(e) => setForm({ ...form, website: e.target.value })} /></label>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium">Nom *</label>
                        <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="contact-last-name" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium">Prénom *</label>
                        <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="contact-first-name" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium">Email *</label>
                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium">Organisation</label>
                        <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="contact-organization" />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-sm font-medium">Sujet *</label>
                    <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        placeholder="ex. Devis pour 5 espaces — secteur Santé"
                        data-testid="contact-subject" />
                </div>

                <div className="space-y-1.5">
                    <label className="text-sm font-medium">Votre message *</label>
                    <Textarea required rows={6} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Décrivez votre besoin, le contexte, et vos attentes…"
                        data-testid="contact-message" />
                    <div className="text-[11px] text-muted-foreground text-right">{form.message.length} / 4000</div>
                </div>

                <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary-hover h-12 w-full sm:w-auto px-8" data-testid="contact-submit">
                    {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                    Envoyer mon message
                </Button>
                <p className="text-[11px] text-muted-foreground">
                    Vos données sont traitées conformément à notre <Link to="/p/privacy" className="text-primary hover:underline">politique de confidentialité</Link>.
                </p>
            </form>
        </div>
    );
}
